import React from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';
import { IContratoData, validateCNPJ } from '../index';
import api from '../../../services/api';
import { FiDownload } from '../../../utils/icons';

interface DadosContratoTabProps {
  data: IContratoData;
  setData: React.Dispatch<React.SetStateAction<IContratoData>>;
  cnpjError: string;
  setCnpjError: (val: string) => void;
  onContratoFileChange: (file: File | null) => void;
  contratoFileName: string;
  contratoId?: number;
  showDownload?: boolean;
}

const RAMOS = [
  'Tecnologia', 'Saúde', 'Financeiro', 'Educação', 'Varejo',
  'Indústria', 'Logística', 'Construção Civil', 'Agronegócio', 'Outro',
];

const TIPOS_AGENTE = ['Controlador', 'Operador', 'Controlador e Operador'];

const formatCNPJ = (value: string) => {
  const nums = value.replace(/\D/g, '').slice(0, 14);
  return nums
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

const DadosContratoTab: React.FC<DadosContratoTabProps> = ({
  data, setData, cnpjError, setCnpjError, onContratoFileChange, contratoFileName,
  contratoId, showDownload,
}) => {
  const handleDownload = async () => {
    if (!contratoId) return;
    const res = await api.getContratoDownloadUrl(contratoId, 'contrato_arquivo');
    if (res.success && res.data?.url) window.open(res.data.url, '_blank');
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setData(prev => ({ ...prev, [name]: value }));
  };

  const handleCNPJ = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData(prev => ({ ...prev, cnpj: formatCNPJ(e.target.value) }));
    setCnpjError('');
  };

  // Valida assim que o usuário sai do campo, em vez de só ao clicar em Salvar.
  const handleCNPJBlur = () => {
    if (!data.cnpj.trim()) return;
    setCnpjError(validateCNPJ(data.cnpj) ? '' : 'CNPJ inválido.');
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    onContratoFileChange(e.target.files?.[0] || null);
  };

  return (
    <Form>
      <Row className="g-3">
        <Col md={6}>
          <Form.Group controlId="razaoSocial">
            <Form.Label>Razão Social <span className="text-danger">*</span></Form.Label>
            <Form.Control type="text" name="razao_social" value={data.razao_social} onChange={handleChange} placeholder="Razão Social" />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="nomeFantasia">
            <Form.Label>Nome Fantasia</Form.Label>
            <Form.Control type="text" name="nome_fantasia" value={data.nome_fantasia} onChange={handleChange} placeholder="Nome Fantasia" />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="cnpj">
            <Form.Label>CNPJ <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="cnpj"
              value={data.cnpj}
              onChange={handleCNPJ}
              onBlur={handleCNPJBlur}
              placeholder="00.000.000/0000-00"
              isInvalid={!!cnpjError}
              maxLength={18}
            />
            <Form.Control.Feedback type="invalid">{cnpjError}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="ramosAtuacao">
            <Form.Label>Ramos de Atuação</Form.Label>
            <Form.Select name="ramos_atuacao" value={data.ramos_atuacao} onChange={handleChange}>
              <option value="">Selecione</option>
              {RAMOS.map(r => <option key={r} value={r}>{r}</option>)}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="ramoEscopo">
            <Form.Label>Ramo no Escopo – Contrato</Form.Label>
            <Form.Control type="text" name="ramo_escopo" value={data.ramo_escopo} onChange={handleChange} placeholder="Ex: Desenvolvimento de software" />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="tipoAgente">
            <Form.Label>Tipo Agente de Tratamento Principal</Form.Label>
            <Form.Select name="tipo_agente" value={data.tipo_agente} onChange={handleChange}>
              <option value="">Selecione</option>
              {TIPOS_AGENTE.map(t => <option key={t} value={t}>{t}</option>)}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="dpo">
            <Form.Label>Encarregado pelo Tratamento de Dados (DPO)</Form.Label>
            <Form.Control type="text" name="dpo" value={data.dpo} onChange={handleChange} placeholder="Nome do DPO" />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="vigenciaInicio">
            <Form.Label>Vigência do Contrato – Início</Form.Label>
            <Form.Control type="date" name="vigencia_inicio" value={data.vigencia_inicio} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="vigenciaFim">
            <Form.Label>Vigência do Contrato – Fim</Form.Label>
            <Form.Control type="date" name="vigencia_fim" value={data.vigencia_fim} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={12}>
          <Form.Group controlId="escopoDescricao">
            <Form.Label>Escopo Descrição</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="escopo_descricao"
              value={data.escopo_descricao}
              onChange={handleChange}
              placeholder="Descreva o escopo do contrato..."
            />
          </Form.Group>
        </Col>
        <Col md={12}>
          <Form.Group controlId="contratoUpload">
            <Form.Label>Contrato (Upload)</Form.Label>
            <Form.Control type="file" accept=".pdf,.doc,.docx" onChange={handleFile} />
            {contratoFileName && (
              <div className="d-flex align-items-center gap-2 mt-1">
                <Form.Text className="text-muted mb-0">Arquivo atual: {contratoFileName}</Form.Text>
                {showDownload && (
                  <Button variant="outline-secondary" size="sm" onClick={handleDownload}><FiDownload /> Baixar</Button>
                )}
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
};

export default DadosContratoTab;
